import { useBranding } from '@/lib/branding';
import {
  type Theme,
  applyDesignTheme,
  applyTheme,
  loadTheme,
  resolveTheme,
  saveTheme,
} from '@/lib/theme';
import { type ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';

interface ThemeContextValue {
  theme: Theme;
  resolved: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  children: ReactNode;
  /** 라우트 그룹별 강제 테마 (예: 어드민은 'dark' 고정). 없으면 사용자 선택을 따름. */
  force?: 'light' | 'dark';
}

export function ThemeProvider({ children, force }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => loadTheme());
  const resolved = force ?? resolveTheme(theme);
  // 디자인 테마는 전역 (관리자 설정). 응답 도착 전엔 DEFAULT_BRANDING('default') 폴백 — 깨지지 않음.
  const { designTheme } = useBranding();

  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  useEffect(() => {
    applyDesignTheme(designTheme);
  }, [designTheme]);

  useEffect(() => {
    if (force) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      if (theme === 'system') applyTheme(resolveTheme('system'));
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme, force]);

  // Cmd/Ctrl + Shift + L 로 라이트/다크 즉시 토글 (force 가 걸린 인스턴스는 스킵).
  useEffect(() => {
    if (force) return;
    const handler = (e: KeyboardEvent) => {
      if (
        (e.metaKey || e.ctrlKey) &&
        e.shiftKey &&
        !e.altKey &&
        (e.code === 'KeyL' || e.key === 'l' || e.key === 'L')
      ) {
        e.preventDefault();
        const next: Theme = resolved === 'dark' ? 'light' : 'dark';
        setThemeState(next);
        saveTheme(next);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [force, resolved]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      resolved,
      setTheme: (next) => {
        setThemeState(next);
        saveTheme(next);
      },
    }),
    [theme, resolved],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
